import { Injectable } from '@angular/core';
import { FeatureState } from '@modeso/aumico-lib-core-fe';
import { OrganizationProvider } from '@modeso/aumico-lib-users-fe';
import { AnnualReportStep } from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { select, Store } from '@ngrx/store';
import Debug from 'debug';
import { filter, map, Observable, of, tap, withLatestFrom } from 'rxjs';
import { FCPState } from '../../../../aumico-lib-users-fe/src/lib/enums/subscription-state.enum';
import * as fromAnnualReportNavigationAction from '../actions/annual-report-navigation.actions';
import { AnnualReportStepsHelper } from '../helpers/annual-report-template-steps-helper';
import * as fromFeature from './../reducer/annual-report-navigation.reducer';
import { IAnnualReportNavigationProvider } from './annual-report-navigation.provider.interface';

const debug = Debug('aumico:aumico-lib-annualreport-fe:TextSnippetProvider');
@UntilDestroy()
@Injectable({
    providedIn: 'root',
})
export class AnnualReportNavigationProvider implements IAnnualReportNavigationProvider {
    constructor(private readonly store: Store<FeatureState>, private organizationProvider: OrganizationProvider) {
        debug('provider constructed');
    }

    getActiveArNavigation$(): Observable<AnnualReportStep[]> {
        return this.store.pipe(select(fromFeature.selectActiveArNavigations));
    }

    // to set the current annual report navigation step that the user is in
    setActiveArNavigation(arNavigation: AnnualReportStep[], isSealed: boolean): void {
        of(arNavigation)
            .pipe(
                withLatestFrom(this.organizationProvider.getCurrentOrganization$().pipe(filter(org => org != null), untilDestroyed(this))),
                map(([arNavigations, organization]) => {
                    if (isSealed) {
                        return [AnnualReportStep.PRINT];
                    }

                    let updatedArNavigations = [...arNavigations];
                    if (organization.fcpActivationDetails != null) {
                        const isFcpActive = [FCPState.PAID, FCPState.TRIAL].includes(
                            organization.fcpActivationDetails.subscriptionState,
                        );
                        updatedArNavigations = AnnualReportStepsHelper.getAnnualReportSteps(arNavigations, isFcpActive);
                    }

                    return updatedArNavigations;
                }),
                tap((updatedArNavigations) => {
                    this.store.dispatch(
                        fromAnnualReportNavigationAction.setActiveArNavigations({ payload: updatedArNavigations }),
                    );
                }),
                untilDestroyed(this)
            )
            .subscribe();
    }

    resetActiveArNavigation(): void {
        this.store.dispatch(fromAnnualReportNavigationAction.resetActiveArNavigation());
    }
    /**
     *
     */
    public getNextNavigation$(currentNavigationStep: AnnualReportStep): Observable<AnnualReportStep> {
        return this.store.pipe(select(fromFeature.selectNextNavigation(currentNavigationStep)));
    }

    public getPreviousNavigation$(currentNavigationStep: AnnualReportStep): Observable<AnnualReportStep> {
        return this.store.pipe(select(fromFeature.selectPreviousNavigation(currentNavigationStep)));
    }

    public isPartOfArNavigation$(targetNavigation: AnnualReportStep): Observable<boolean> {
        return this.store.pipe(select(fromFeature.isPartOfArNavigation(targetNavigation)));
    }
}
